import React from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import { Check } from "lucide-react";

export default function PhaseProgress() {
  const { phase, goTo } = useSimulation();
  const { t } = useI18n();

  const phases = [
    { n: 1, label: t("phases.p1") },
    { n: 2, label: t("phases.p2") },
    { n: 3, label: t("phases.p3") },
    { n: 4, label: t("phases.p4") },
    { n: 5, label: t("phases.p5") },
  ];

  return (
    <div
      data-testid="phase-progress"
      className="w-full border-b border-[color:var(--border)] bg-[color:var(--bg)]/80 backdrop-blur-md sticky top-[64px] z-30 no-print"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        {/* Mobile / tablet: numbered chips + active label */}
        <div className="lg:hidden flex items-center justify-between gap-1.5 sm:gap-2">
          {phases.map((p) => {
            const isActive = p.n === phase;
            const isDone = p.n < phase;
            return (
              <button
                key={p.n}
                onClick={() => goTo(p.n)}
                data-testid={`phase-nav-${p.n}`}
                aria-label={`${t("common.goToPhase")} ${p.n}`}
                className={[
                  "flex items-center gap-2 min-w-0 shrink-0 h-9 px-2 sm:px-3 border font-mono text-xs sm:text-sm transition-colors rounded-sm",
                  isActive
                    ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-black"
                    : isDone
                      ? "border-[color:var(--brand)] text-[color:var(--brand)]"
                      : "border-[color:var(--border-strong)] text-[color:var(--text-dim)]",
                ].join(" ")}
              >
                {isDone ? <Check className="w-3.5 h-3.5" /> : <span>{p.n.toString().padStart(2, "0")}</span>}
                {isActive && (
                  <span className="hidden sm:inline text-xs font-display tracking-wide truncate max-w-[9rem]">
                    {p.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Desktop lg+: full numbered chips with labels and connectors */}
        <div className="hidden lg:flex items-center justify-between gap-3">
          {phases.map((p, idx) => {
            const isActive = p.n === phase;
            const isDone = p.n < phase;
            return (
              <React.Fragment key={p.n}>
                <button
                  onClick={() => goTo(p.n)}
                  data-testid={`phase-nav-${p.n}-lg`}
                  aria-label={`${t("common.goToPhase")} ${p.n}`}
                  className="flex items-center gap-3 min-w-0 text-left"
                >
                  <span
                    className={[
                      "flex items-center justify-center w-8 h-8 border font-mono text-sm shrink-0 transition-colors",
                      isActive
                        ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-black"
                        : isDone
                          ? "border-[color:var(--brand)] text-[color:var(--brand)]"
                          : "border-[color:var(--border-strong)] text-[color:var(--text-dim)]",
                    ].join(" ")}
                  >
                    {isDone ? <Check className="w-4 h-4" /> : p.n.toString().padStart(2, "0")}
                  </span>
                  <span
                    className={[
                      "text-sm font-display tracking-wide whitespace-nowrap",
                      isActive ? "text-[color:var(--text)]" : isDone ? "text-[color:var(--text-dim)]" : "text-[color:var(--text-mute)]",
                    ].join(" ")}
                  >
                    {p.label}
                  </span>
                </button>
                {idx < phases.length - 1 && (
                  <span
                    className={[
                      "h-px flex-1 min-w-[16px]",
                      isDone ? "bg-[color:var(--brand)]" : "bg-[color:var(--border)]",
                    ].join(" ")}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
